/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import Breadcrumb, { Item } from '@/components/common/Breadcrumb';
import { ROUTE } from '@/routes';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AddUserForm from './components/AddEditUser';
import { AxiosError } from 'axios';
import { buildAuditEvent, extractErrorDetails } from '@/lib/utils';
import { ErrorToast } from '@/pages/AddEditDoctor/components';
import { UserFormInputs } from './components/validationSchema';
import { mixPanelServiceInstance } from '@/analytics/mixpanel';
import { mixPanelEvents } from '@/analytics/mixpanel/events';
import { addUser, fetchSingleUser, updateUser } from '../UserList/userApi';
import { userPayload, users, usersAM } from '../UserList/types';
import useUserInfo from '@/components/Authenticate/useUserInfo';
import { toast } from '@/components/hooks/use-toast';
import { ROLES_LIST } from '@/constants';
import { AuditEventAPI } from '@/lib/axios/apis/recordManagement/auditEvent';

export default function AddUser() {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [userProfile, setUserProfile] = useState<any>();
  const { id: amUserId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const { state } = useLocation();
  const { userInfo } = useUserInfo();

  useEffect(() => {
    if (!amUserId) return;
    (async () => {
      const userData = await fetchSingleUser(amUserId);
      setUserProfile(userData?.usersData);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amUserId]);

  const breadcrumbItems = () => {
    return [
      { label: 'User Management', path: ROUTE.USER_MANAGEMENT },
      {
        label: state ? 'View Profile' : 'Edit Profile',
        path: amUserId
          ? `/user-management/${amUserId}/user-profile`
          : ROUTE.ADD_USER,
      },
    ];
  };

  const handleNavigation = (item: Item) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  function mergeObjects(obj1: usersAM, obj2: users): userPayload {
    return {
      firstName: obj2.firstName,
      lastName: obj2.lastName,
      email: obj2.fluentEmail || obj1.email,
      additionalInformation: {
        secondary_email: obj2.personalEmail,
        phone_number: obj2.phone_number,
        permify_roles: obj2.roles,
      },
      enabled: true,
      mode: 'verify',
    };
  }

  const onSubmit = async (data: UserFormInputs) => {
    try {
      const finalUser = mergeObjects(userProfile, data);

       const roleMap = new Map(ROLES_LIST?.map(({ value, label }) => [value, label]));
      
            const displayNames = finalUser?.additionalInformation?.permify_roles?.map(role => roleMap.get(role));
            
            const result = displayNames?.join(", ");

      if (userProfile?.additionalInformation?.emr_id) {
        await updateUser(amUserId, finalUser);
        const auditEvent = buildAuditEvent({
          eventType: 'create',
          practitionerId: `${userInfo?.additionalInformation?.emr_id}`,
          practitionerName: `${userInfo?.firstName} ${userInfo?.lastName}`,
          ipAddress: '192.168.2.50',
          textStatus: `${userProfile?.firstName} ${userProfile?.lastName}`,
          textDiv: `${userInfo?.firstName} edited ${userProfile?.firstName}'s profile.`,
        });
        await AuditEventAPI.postAuditEvent(auditEvent)
        mixPanelServiceInstance.track(
          mixPanelEvents.USER_EVENTS.EDIT_USER_COMPLETED,
          {
            user_manager_id: userInfo?.id || 'N/A',
            // ur_first_name: data.firstName,
            // ur_last_name: data.lastName,
            // ur_phone_number: data.phone_number,
            // ur_personal_email: data.personalEmail,
            // ur_role: data.roles,
            // ur_fluent_email: data.fluentEmail,
            user_id: amUserId,
          }
        );
        toast({
          title: 'User details updated',
          variant: 'default',
        });
      } else {
        await addUser(finalUser);
        mixPanelServiceInstance.track(
          mixPanelEvents.USER_EVENTS.ADD_USER_COMPLETED,
          {
            user_manager_id: amUserId || 'N/A',
            ur_first_name: data.firstName,
            ur_last_name: data.lastName,
            ur_phone_number: data.phone_number,
            ur_personal_email: data.personalEmail,
            ur_role: data.roles,
            ur_fluent_email: data.fluentEmail,
          }
        );
      }
      const auditEvent = buildAuditEvent({
        eventType: 'create',
        practitionerId: `${userInfo?.additionalInformation?.emr_id}`,
        practitionerName: `${userInfo?.firstName} ${userInfo?.lastName}`,
        ipAddress: '192.168.2.50',
        textStatus: `${userProfile?.firstName} ${userProfile?.lastName}`,
        textDiv: `${userInfo?.firstName} created user profile for ${userProfile?.firstName} ${userProfile?.lastName} and assigned roles ${result}`,
      });
      await AuditEventAPI.postAuditEvent(auditEvent)
      navigate(ROUTE.USER_MANAGEMENT);
      toast({
        title: 'User details updated',
        variant: 'default',
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const extractedDetails = extractErrorDetails(
          error.response?.data?.message
        );
        const errors = {
          errorMessageField: { message: extractedDetails.errorMessage },
          traceIdField: { message: `Trace ID: ${extractedDetails.traceId}` },
        };
        ErrorToast({ errors });
      } else {
        ErrorToast({
          errors: { unknownField: { message: 'An unknown error occurred' } },
        });
      }
    }
  };

  const handleEditToggle = (editMode: boolean) => {
    setIsEditing(editMode);
  };

  return (
    <div
      className="relative p-8 h-full overflow-y-scroll"
      style={{ background: '#f8f8ff' }}
    >
      <Breadcrumb items={breadcrumbItems()} onClick={handleNavigation} />
      <div className="mt-5 gap-4 flex flex-col">
        {isEditing && (
          <div className="text-[28px] font-p22Mackinac text-[#000] font-normal mt-2">
            Edit User
          </div>
        )}
        <div className="bg-white px-6 relative">
          {userProfile ? (
            <AddUserForm
              userManagerId={amUserId}
              onFormSubmit={onSubmit}
              onEditToggle={handleEditToggle}
              userProfile={userProfile}
              state={state}
            />
          ) : (
            <div>Loading user profile...</div>
          )}
        </div>
      </div>
    </div>
  );
}
